import { MouseEvent, ReactNode, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, componentWithRef, Typography } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { IconType } from '@robocorp/icons';
import { IconChevronRight } from '@robocorp/icons/iconic';

import { useValuesContext } from '../context';
import { ValuesSize } from '../types';

type Props = {
  label: string;
  children?: ReactNode;
  onClick?: () => void;
  icon?: IconType;
  size?: ValuesSize;
  to?: string;
};

const Container = styled.li<{ $size: ValuesSize }>`
  list-style: none;

  > button,
  > span,
  > a {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: left;
    background: none;
    border-radius: ${({ theme }) => theme.radii.$12};
    height: 100%;
    width: 100%;

    padding: ${({ theme, $size }) => {
      // TODO: expose variants from @robocorp/ds or move Values to DS
      switch ($size) {
        case 'small':
          return `${theme.space.$4} ${theme.space.$8}`;
        case 'large':
          return `${theme.space.$8} ${theme.space.$16}`;
        case 'medium':
        default:
          return `${theme.space.$4} ${theme.space.$8}`;
      }
    }};
  }

  .icon-container {
    width: ${({ theme }) => theme.sizes.$24};
    height: ${({ theme }) => theme.sizes.$24};
    border-radius: 100%;
    background: ${({ theme }) => theme.colors.background.subtle.color};
  }

  button,
  a {
    color: ${({ theme }) => theme.colors.content.primary.color};
    &:hover {
      background: ${({ theme }) => theme.colors.background.subtle.color};

      strong {
        color: ${({ theme }) => theme.colors.content.accent.color};
      }

      > div > span path {
        stroke: ${({ theme }) => theme.colors.content.accent.color};
      }

      .icon-container {
        color: ${({ theme }) => theme.colors.content.inversed.color};
        background: ${({ theme }) => theme.colors.background.inversed.color};
        margin-left: auto;
      }
    }
  }

  div > span {
    font-weight: inherit;
  }
`;

/**
 * Values helper component to display a single statistical value, optionally as an interactive action button
 *
 * @example
 * <Values.Item label="Label" onClick={onClick}>Value</Values.Item>
 */
export const Item = componentWithRef<Props, HTMLLIElement>(
  ({ children, icon, size: sizeLocal, label, onClick, to }, forwardedRef) => {
    const { size: sizeGlobal } = useValuesContext();
    const navigate = useNavigate();

    const size = sizeLocal || sizeGlobal;
    const Icon = to ? (icon ?? IconChevronRight) : icon;

    const componentType = useMemo(() => {
      if (to) {
        return 'a';
      }
      if (typeof onClick === 'function') {
        return 'button';
      }
      return 'span';
    }, [onClick, to]);

    const titleVariant = useMemo(() => {
      switch (size) {
        case 'large':
          return 'display.x2-large';
        case 'small':
          return 'body.medium';
        case 'medium':
        default:
          return 'display.headline';
      }
    }, [size]);

    const titleFontWeight = useMemo(() => {
      switch (size) {
        case 'large':
          return 'bold';
        default:
          return 'semibold';
      }
    }, [size]);

    const handleClick = useCallback(
      (e: MouseEvent) => {
        if (to) {
          e.preventDefault();
          navigate(to);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [to],
    );

    return (
      <Container role="listitem" ref={forwardedRef} $size={size}>
        <Box as={componentType} href={to} flex="1" onClick={onClick || handleClick}>
          <Box display="flex" height="$32" alignItems="center" gap="$8">
            <Typography fontWeight="medium" color="content.subtle">
              {label}
            </Typography>
            {Icon && (
              <div className="icon-container">
                <Icon />
              </div>
            )}
          </Box>
          <Typography
            as="div"
            variant={titleVariant}
            lineHeight="1.4em"
            fontWeight={titleFontWeight}
            truncate={2}
          >
            {children}
          </Typography>
        </Box>
      </Container>
    );
  },
);
